import React, {useEffect, useState} from 'react';
import {Alert, Button, ColumnDataType, DataTable, Spinner,} from "@tag/tag-components-react-v4";
import AppGroupService from "../../APP/services/AppGroupService";
import {CrosshairFilled, MergeFilled} from "@tag/tag-icons";
import {AppGroupOctopusSync} from "../../APP/types/AppGroupOctopusSync";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import AppRolesService from "../../General/services/AppRolesService";
import styles from './APPOctoSync.module.scss';
import APPOctoSyncAllModal from "./APPOctoSyncAllModal";

const APPOctoSync = () => {
    const [appGroups, setAppGroups] = useState<AppGroupOctopusSync[]>([]);
    const [loading, setLoading] = useState<boolean>();
    const [syncAllModalVisible, setSyncAllModalVisible] = useState<boolean>(false);
    const [alert, setAlert] = useState<{type: any, message: string}>();

    const { accounts, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    while (!inProgress) {
        if (!AppRolesService.isUserAdmin(accounts, isAuthenticated)) {
            throw new Error()
        }
    }
    
    useEffect(() => {
        setLoading(true);
        AppGroupService.getAppGroups()
            .then((response) => {
                setAppGroups(response)
                setLoading(false);
            });
    }, []);
    
    const performAppGrpSync = (appGroup: string) => {
        AppGroupService.octopusSyncForAppGroup(appGroup)
            .then((result) => {
                const updatedList = appGroups.map(ag => {
                    if (ag.name === appGroup) {
                        return {
                            name: ag.name,
                            serverId: ag.serverId,
                            updated: true,
                            created: true,
                            deleted: true,
                        };
                    }
                    return ag;
                });
                
                setAppGroups(updatedList);
            })
            .catch(() => {
                setLoading(false);
                setAlert({type: 'error', message: 'There was an issue with the sync, please investigate'});
            })
    }
    
    const getData = () => {
        let shaded = false;
        return appGroups.map((ag) => {
            shaded = !shaded;
            
            return {
                appGroup: ag.name,
                shaded: shaded,
                deletedTenantCount: ag.deleted,
                updatedTenantCount: ag.updated,
                createdTenantCount: ag.created
            }
        });
    }

    const renderSyncButton = ({row}: any) => {
            return (
                <Button
                    icon={<MergeFilled />}
                    style={{backgroundColor: '#aee9e9'}}
                    onClick={() => {
                        performAppGrpSync(row.original.appGroup);
                    }}
                />
            )
        }

    const getColumns = () => {
        return [
            { accessorKey: 'appGroup', header: 'App Group', meta: { dataType: 'string' as ColumnDataType } },
            { accessorKey: 'sync', header: 'Sync', cell: renderSyncButton },
            { accessorKey: 'deletedTenantCount', header: 'Deleted', meta: { dataType: 'boolean' as ColumnDataType } },
            { accessorKey: 'updatedTenantCount', header: 'Updated', meta: { dataType: 'boolean' as ColumnDataType } },
            { accessorKey: 'createdTenantCount', header: 'Created', meta: { dataType: 'boolean' as ColumnDataType } },
        ]
    }

    let header = (
        <div style={{marginLeft: '2em'}}>
            <Button
                className={styles.OctopusSyncBackButton}
                onClick={() => setSyncAllModalVisible(true)}
                icon={<CrosshairFilled />}
            >Sync All</Button>
            <h1 className={styles.OctopusSyncHeader}>Octopus Sync</h1>
        </div>
    )
    let content = null;
    if (loading) {
        content = <Spinner full />;
    }
    else {
        content = (
            <DataTable
                scrollerProps={{
                    style:{ width: 'unset' } // Temporary while scroller not needed
                }}
                getRowProps={(row) => ({
                    style: {
                        backgroundColor: row.original.shaded ? '#FEFAEC' : '#F3F1FA'
                    },
                })}
                enableSorting={true}
                columns={getColumns()}
                data={getData()}
            />
        );
    }

    return (
        <>
            {header}
            {content}
            {syncAllModalVisible ?
                <APPOctoSyncAllModal
                    visible={syncAllModalVisible}
                    handleClose={() => setSyncAllModalVisible(false)}
                    setAlert={setAlert}
                    setLoading={setLoading}
                /> : undefined
            }
            {alert ?
                <Alert
                    className={styles.CustomerListAlert}
                    closeTransition="fade"
                    type={alert.type}
                    showDuration={1200}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert> : undefined
            }
        </>
    );
}

export default APPOctoSync;
